import { colors, FITPET_CDN_URL } from '@fitpet/constants-global'
import { FitpetIcon } from '@fitpet/ui/components/fitpet-icon'
import { clsx } from 'clsx'
import { HomeSectionDisplayCollectionResponseDto } from '@/apis/rest/generated/types'
import { StyledDivider } from '@/components/common/divider'
import { HorizontalScrollContainer } from '@/components/domains/etc/horizontal-scroll-container'
import { HomeButtonSeeMore } from '@/components/domains/home'
import { Anchor } from '@/components/domains/home/Anchor'
import { THUMBNAIL_SIZES, THUMBNAIL_STATES } from '@/components/domains/products/new-product-card/new-product-card.type'
import { VerticalProductCard } from '@/components/domains/products/new-product-card/vertical-product-card'
import { FitpetMallEventEnum } from '@/constants/fitpet-mall-event.enum'
import ROUTES from '@/constants/legacy/constRoutes'
import { QueryStringKeyEnum } from '@/constants/query-string-key.enum'
import { useTracker } from '@/containers/contexts/EventTrackingProvider'
import { useIntersectionObserver } from '@/containers/hooks'
import useCustomRouter from '@/containers/hooks/useCustomRouter'
import { ProductTypeDefinedByFrontend } from '@/types/product-type-defined-by.frontend'
import { Base64ConverterUtil } from '@/utils/base64-converter.util'
import { transformProductTypeDefinedByFrontend } from '@/utils/product/product-card.util'
import { currencyText } from '@/utils/utilNumber'
import styles from './home-display-collection-magazine-background-image.module.css'

const minLength = 1
const maxLength = 20

interface HomeDisplayCollectionMagazineBackgroundImageProps {
  homeSectionItem: HomeSectionDisplayCollectionResponseDto
  refCallback: (node: HTMLDivElement) => void
}

export const HomeDisplayCollectionMagazineBackgroundImage = ({
  homeSectionItem,
  refCallback,
}: HomeDisplayCollectionMagazineBackgroundImageProps) => {
  if (!homeSectionItem.displayCollection) {
    return null
  }

  if (
    !homeSectionItem.products ||
    homeSectionItem.products.length === 0 ||
    homeSectionItem.products.length < minLength
  ) {
    return null
  }
  const { push } = useCustomRouter()

  const tracker = useTracker()
  const { displayCollection, products } = homeSectionItem

  const title = displayCollection.name

  const { elementRef } = useIntersectionObserver(
    () => {
      tracker.triggerCustomEvent(FitpetMallEventEnum.ViewHomeSection, { sectionName: title })
    },
    { rootMargin: '0px' }
  )

  const seeMoreLink = {
    pathname: ROUTES.SECTION_DETAIL,
    query: {
      displayCollectionId: Base64ConverterUtil.convertNumberIdToBase64ByKey({
        key: 'DisplayCollectionType',
        id: displayCollection?.id,
      }),
      title,
    },
  }

  const handleClickProductCard = (product: ProductTypeDefinedByFrontend) => {
    tracker.triggerCustomEvent(FitpetMallEventEnum.ClickHomeMagazineBackgroundImageProduct, {
      productId: product.id,
      productName: product.name,
      productPurchasePrice: product.salePrice,
    })
    push({
      pathname: ROUTES.PRODUCTS.DETAIL,
      query: {
        productId: product.id,
        promotion: Base64ConverterUtil.convertNumberIdToBase64ByKey({
          key: 'ProductPromotionType',
          id: product.promotion?.id,
        }),
        ...(title && { [QueryStringKeyEnum.SectionName]: title }),
      },
    })
  }

  return (
    <>
      <div
        className={styles.container}
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.15), rgba(0, 0, 0, 0.15)), url(${FITPET_CDN_URL}/${displayCollection.backgroundImage})`,
        }}
        ref={elementRef}
      >
        <Anchor id={title} refCallback={refCallback} />
        <div className={styles.textArea}>
          <div className={clsx(styles.seoTitle, 'font-heading5-bold gray-white')}>{title}</div>
          <div className={clsx(styles.description, 'font-body3-medium gray-white')}>
            {displayCollection.description}
          </div>
        </div>
        <HorizontalScrollContainer>
          <div className={styles.productCardList}>
            {products?.slice(0, maxLength).map((product) => {
              const productData = transformProductTypeDefinedByFrontend(product)
              return (
                <div key={productData.id} style={{ position: 'relative' }}>
                  <VerticalProductCard size={THUMBNAIL_SIZES.lg} onClick={() => handleClickProductCard(productData)}>
                    <VerticalProductCard.Thumbnail
                      productId={productData.id}
                      productName={productData.name}
                      productPromotionScheme={productData.promotion}
                      thumbnailState={THUMBNAIL_STATES.basicLabel}
                      imageUrl={productData.thumbnail}
                      isLiked={productData.isLiked}
                      isRealSoldOut={productData.isRealSoldOut}
                      isNotAvailable={productData.isNotAvailable}
                      trackingData={productData.thumbnailData}
                      sticker={productData.sticker}
                    />
                    <div className={clsx(styles.productName, 'font-body3-medium gray-white')}>{productData.name}</div>
                    <div className="font-body1-bold gray-white">{currencyText(productData.salePrice)}</div>
                    <div className={styles.shippingFee}>
                      <FitpetIcon icon="icShippingFee" size={16} color={colors.WHITE} />
                      <span className="font-body5-bold gray-white">
                        {productData.expectedShippingFee > 0
                          ? currencyText(productData.expectedShippingFee)
                          : '무료배송'}
                      </span>
                    </div>
                  </VerticalProductCard>
                </div>
              )
            })}
          </div>
        </HorizontalScrollContainer>
        <HomeButtonSeeMore className={'btn-see-all'} seeMoreLink={seeMoreLink} isWhiteFont />
      </div>
      <StyledDivider height="0.1rem" backgroundColor={colors.GRAY_70} />
    </>
  )
}
